import { isAxiosError } from 'axios';
import { Trash } from 'lucide-react';
import { Fragment, useEffect, useMemo, useRef } from 'react';
import { type Path, useFieldArray, useForm } from 'react-hook-form';

import {
  useDeviceScanRanges,
  useDeviceSettings,
  useIntl,
  useUpdateDeviceScanRanges,
} from '../../hooks';
import { hzToMhz, mhzToHz } from '../../lib/frequency.js';
import { cn } from '../../lib/utils.js';
import { zodResolver } from '../../lib/zod-resolver.js';
import {
  DeviceFrequencyRangeForm,
  mapChannelsFromApi,
  mapRangesFromApi,
} from '../../services/device-scan-ranges.js';
import { Device } from '../../types';
import type { FrequencyChannel } from '../../types/frequency-channel.js';
import type { FrequencyRange } from '../../types/frequency-range.js';
import type { ScanRanges } from '../../types/scan-ranges.js';
import {
  buildScanRangesSchema,
  type ChannelField,
  type DeviceScanRangesFormValues,
  isChannelField,
  isRangeField,
  type RangeField,
} from '../../validations/device-scan-ranges.js';
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Input,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui';

type Props = {
  device: Device;
};

const errorInputClass =
  'border-destructive focus-visible:ring-destructive focus-visible:ring-offset-2';

const channelFieldPath = (index: number, field: ChannelField): Path<DeviceScanRangesFormValues> =>
  `channels.${index}.${field}` as Path<DeviceScanRangesFormValues>;

const rangeFieldPath = (index: number, field: RangeField): Path<DeviceScanRangesFormValues> =>
  `ranges.${index}.${field}` as Path<DeviceScanRangesFormValues>;

export const sanitizeChannelsJson = (raw: string): string => {
  const noComments = raw
    .split(/\r?\n/)
    .filter((line) => {
      const trimmed = line.trim();
      return !(trimmed.startsWith('#') || trimmed.startsWith('###') || trimmed.startsWith('//'));
    })
    .join('\n');
  return noComments.replace(/,\s*([\]}])/g, '$1');
};

const toRecord = (value: unknown): Record<string, unknown> => {
  if (typeof value === 'object' && value !== null) {
    return value as Record<string, unknown>;
  }
  return {};
};

const toNumber = (value: unknown, fallback = 0): number => {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : fallback;
};

const toStringSafe = (value: unknown): string => {
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  return '';
};

export const normalizeChannelFromFile = (raw: unknown): FrequencyChannel => {
  const item = toRecord(raw);
  return {
    band: toStringSafe(item['band']),
    group: toStringSafe(item['group']),
    channel: toNumber(item['channel']),
    freq: toNumber(item['freq'] ?? item['frequency']),
    isDigital: Boolean(item['isDigital'] ?? item['is_digital'] ?? item['digital'] ?? false),
    enabled: Boolean(item['enabled']),
  };
};

export const normalizeRangeFromFile = (raw: unknown): DeviceFrequencyRangeForm => {
  const item = toRecord(raw);
  return {
    startFrequency: toNumber(item['startFrequency'] ?? item['start_frequency'] ?? item['start']),
    endFrequency: toNumber(item['endFrequency'] ?? item['end_frequency'] ?? item['end']),
    step: (() => {
      const rawStep = toNumber(item['step'], 10);
      return rawStep > 0 ? rawStep : 10;
    })(),
    isDigital: Boolean(item['isDigital'] ?? item['is_digital'] ?? item['digital'] ?? false),
    enabled: Boolean(item['enabled']),
  };
};

export default function DeviceScanRangesPanel({ device }: Props) {
  const intl = useIntl();
  const updateScanRanges = useUpdateDeviceScanRanges();
  const {
    data: latestScanRanges,
    isLoading,
    isFetching,
    error,
    refetch,
  } = useDeviceScanRanges(device.id);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const initialValues = useMemo<DeviceScanRangesFormValues>(() => {
    return {
      channels: mapChannelsFromApi(
        latestScanRanges?.channels ?? [],
      ) as DeviceScanRangesFormValues['channels'],
      ranges: mapRangesFromApi(
        latestScanRanges?.ranges ?? [],
      ) as DeviceScanRangesFormValues['ranges'],
    };
  }, [latestScanRanges]);

  const { data: deviceSettings } = useDeviceSettings(device.id);

  const validationSchema = useMemo(() => {
    let minAllowedMhz = null;
    let maxAllowedMhz = null;

    if (deviceSettings?.minAllowedFrequency) {
      const mhz = hzToMhz(deviceSettings.minAllowedFrequency);
      minAllowedMhz = mhz > 0 ? mhz : null;
    }

    if (deviceSettings?.maxAllowedFrequency) {
      const mhz = hzToMhz(deviceSettings.maxAllowedFrequency);
      maxAllowedMhz = mhz > 0 ? mhz : null;
    }

    return buildScanRangesSchema(minAllowedMhz, maxAllowedMhz);
  }, [deviceSettings?.minAllowedFrequency, deviceSettings?.maxAllowedFrequency]);

  const {
    control,
    handleSubmit,
    register,
    reset,
    setError,
    clearErrors,
    setValue,
    watch,
    formState: { errors, isValid },
  } = useForm<DeviceScanRangesFormValues>({
    resolver: zodResolver(validationSchema),
    mode: 'onChange',
    defaultValues: initialValues,
  });
  const {
    fields: channelFields,
    append: appendChannel,
    remove: removeChannel,
    replace: replaceChannels,
  } = useFieldArray({ control, name: 'channels' });
  const {
    fields: rangeFields,
    append: appendRange,
    remove: removeRange,
    replace: replaceRanges,
  } = useFieldArray({ control, name: 'ranges' });

  const watchedChannels = watch('channels');
  const watchedRanges = watch('ranges');

  useEffect(() => {
    reset(initialValues);
    replaceChannels(initialValues.channels);
    replaceRanges(initialValues.ranges);
    clearErrors();
  }, [initialValues, reset, replaceChannels, replaceRanges, clearErrors]);

  const groupedChannels = useMemo(() => {
    const channels = watchedChannels ?? initialValues.channels;
    return channels.reduce<Record<string, number[]>>((acc, channel, index) => {
      const key = channel?.band?.trim() || '—';
      if (!acc[key]) acc[key] = [];
      acc[key].push(index);
      return acc;
    }, {});
  }, [watchedChannels, initialValues.channels]);

  const handleImportFromText = (text: string) => {
    try {
      const sanitized = sanitizeChannelsJson(text);
      const parsed = JSON.parse(sanitized);

      if (Array.isArray(parsed)) {
        const normalizedChannels = parsed.map(
          normalizeChannelFromFile,
        ) as DeviceScanRangesFormValues['channels'];
        replaceChannels(normalizedChannels);
        setValue('channels', normalizedChannels, { shouldDirty: true, shouldValidate: true });
        replaceRanges([]);
        setValue('ranges', [], { shouldDirty: true, shouldValidate: true });
        return;
      }

      if (parsed && typeof parsed === 'object') {
        const fileData = parsed as {
          channels?: unknown;
          channels_table?: unknown;
          ranges?: unknown;
          channels_ranges?: unknown;
        };
        const channelsSource = fileData.channels ?? fileData.channels_table;
        const rangesSource = fileData.ranges ?? fileData.channels_ranges;

        if (Array.isArray(channelsSource)) {
          const normalizedChannels = channelsSource.map(
            normalizeChannelFromFile,
          ) as DeviceScanRangesFormValues['channels'];
          replaceChannels(normalizedChannels);
          setValue('channels', normalizedChannels, { shouldDirty: true, shouldValidate: true });
        }

        if (Array.isArray(rangesSource)) {
          const normalizedRanges = rangesSource.map(
            normalizeRangeFromFile,
          ) as DeviceScanRangesFormValues['ranges'];
          replaceRanges(normalizedRanges);
          setValue('ranges', normalizedRanges, { shouldDirty: true, shouldValidate: true });
        }

        if (!Array.isArray(channelsSource) && !Array.isArray(rangesSource)) {
          throw new Error('File must contain channels or ranges arrays');
        }

        return;
      }

      throw new Error('File must contain a JSON array or object');
    } catch (err) {
      console.error('Failed to import channels:', err);
      alert(intl.formatMessage({ id: 'deviceSettings.scanRanges.importError' }));
    }
  };

  const handleImportClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange: React.ChangeEventHandler<HTMLInputElement> = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const text = String(reader.result ?? '');
      handleImportFromText(text);
    };
    reader.readAsText(file);
    e.target.value = '';
  };

  const onSubmit = handleSubmit(async (values) => {
    clearErrors();

    const payload: ScanRanges = {
      channels: values.channels.map((channel: FrequencyChannel) => {
        const payload: FrequencyChannel = {
          band: channel.band,
          group: channel.group,
          channel: Number(channel.channel) || 0,
          freq: mhzToHz(Number(channel.freq) || 0),
          isDigital: Boolean(channel.isDigital),
          enabled: Boolean(channel.enabled),
        };

        return payload;
      }),
      ranges: values.ranges.map((range: FrequencyRange) => {
        const stepMhz = Number(range.step) || 10;
        const payload: FrequencyRange = {
          startFrequency: mhzToHz(Number(range.startFrequency) || 0),
          endFrequency: mhzToHz(Number(range.endFrequency) || 0),
          step: mhzToHz(stepMhz > 0 ? stepMhz : 10),
          enabled: Boolean(range.enabled),
          isDigital: Boolean(range.isDigital),
        };

        return payload;
      }),
    };

    try {
      await updateScanRanges.mutateAsync({ id: device.id, scanRanges: payload });
    } catch (err) {
      console.error('Failed to update frequency scanRanges:', err);

      if (isAxiosError(err) && err.response?.data) {
        const data = err.response.data as {
          message?: string;
          issues?: Array<{ path?: Array<string | number>; message?: string }>;
        };

        let issuesApplied = false;

        if (Array.isArray(data.issues)) {
          data.issues.forEach((issue) => {
            if (!issue) return;
            const path = Array.isArray(issue.path) ? issue.path : [];
            const [first, second] = path;

            if (typeof first !== 'number' || typeof second !== 'string') {
              return;
            }

            if (isChannelField(second)) {
              setError(channelFieldPath(first, second), {
                type: 'remote',
                message: issue.message ?? data.message ?? 'Invalid channel entry',
              });
              issuesApplied = true;
              return;
            }

            if (isRangeField(second)) {
              setError(rangeFieldPath(first, second), {
                type: 'remote',
                message: issue.message ?? data.message ?? 'Invalid range entry',
              });
              issuesApplied = true;
            }
          });
        }

        if (data.message) {
          setError('root', { type: 'remote', message: data.message });
        } else if (!issuesApplied) {
          setError('root', {
            type: 'remote',
            message: 'Failed to update frequency Scan Ranges',
          });
        }
        return;
      }

      setError('root', {
        type: 'remote',
        message: err instanceof Error ? err.message : 'Failed to update Scan Ranges',
      });
    }
  });

  const handleClearChannels = () => {
    replaceChannels([]);
    setValue('channels', [], { shouldDirty: true, shouldValidate: true });
  };

  const handleClearRanges = () => {
    replaceRanges([]);
    setValue('ranges', [], { shouldDirty: true, shouldValidate: true });
  };

  if (error) {
    return (
      <div className="flex-1 flex flex-col">
        <Card className="flex-1 flex flex-col">
          <CardHeader>
            <CardTitle>
              {device.serial} - {intl.formatMessage({ id: 'deviceSettings.scanRanges.title' })}
            </CardTitle>
            <CardDescription className="text-destructive">
              {intl.formatMessage({ id: 'deviceSettings.scanRanges.loadError' })}:{' '}
              {error instanceof Error
                ? error.message
                : intl.formatMessage({ id: 'common.unknownError' })}
            </CardDescription>
          </CardHeader>
          <CardContent className="flex-1 flex items-center justify-center">
            <Button variant="outline" size="sm" onClick={() => refetch()}>
              {intl.formatMessage({ id: 'common.retry' })}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const disableInputs = updateScanRanges.isPending || isFetching;

  return (
    <form onSubmit={onSubmit} className="h-full flex flex-col">
      <div className="flex-1 overflow-y-auto">
        <Card className="border-0 shadow-none">
          <CardHeader className="flex flex-col gap-1 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <CardTitle>
                {device.serial} - {intl.formatMessage({ id: 'deviceSettings.scanRanges.title' })}
              </CardTitle>
              <CardDescription>
                {intl.formatMessage({ id: 'deviceSettings.scanRanges.description' })}
              </CardDescription>
            </div>
          </CardHeader>

          <CardContent>
            {isLoading && !latestScanRanges ? (
              <div className="text-sm text-foreground mb-4">
                {intl.formatMessage({ id: 'deviceSettings.scanRanges.loading' })}
              </div>
            ) : null}

            {errors.root?.message ? (
              <div className="text-sm text-destructive mb-4">{errors.root.message}</div>
            ) : null}

            <div className="flex-1 flex flex-col overflow-hidden space-y-6">
              {/* Channels Section */}
              <div className="flex-1 flex flex-col min-h-0">
                <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between mb-3">
                  <div className="text-sm font-medium">
                    {intl.formatMessage({ id: 'deviceSettings.scanRanges.channels' })}
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept=".json,.txt,application/json,text/plain"
                      onChange={handleFileChange}
                      style={{ display: 'none' }}
                    />
                    <Button
                      size="sm"
                      variant="outline"
                      type="button"
                      onClick={handleImportClick}
                      disabled={disableInputs}
                    >
                      {intl.formatMessage({ id: 'deviceSettings.scanRanges.import' })}
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      type="button"
                      onClick={handleClearChannels}
                      disabled={disableInputs}
                    >
                      {intl.formatMessage({ id: 'deviceSettings.scanRanges.clear' })}
                    </Button>
                    <Button
                      size="sm"
                      variant="secondary"
                      type="button"
                      onClick={() =>
                        appendChannel({
                          band: '',
                          group: '',
                          channel: 0,
                          freq: 0,
                          isDigital: false,
                          enabled: true,
                        })
                      }
                      disabled={disableInputs}
                    >
                      {intl.formatMessage({ id: 'deviceSettings.scanRanges.addChannel' })}
                    </Button>
                  </div>
                </div>

                <div className="flex-1 overflow-y-auto rounded-md">
                  {watchedChannels.length === 0 ? (
                    <div className="text-sm text-foreground p-4">
                      {intl.formatMessage({ id: 'deviceSettings.scanRanges.noChannels' })}
                    </div>
                  ) : (
                    <Table>
                      <TableHeader className="sticky top-0 bg-background-light z-10">
                        <TableRow>
                          <TableHead className="w-24 text-center">
                            {intl.formatMessage({ id: 'deviceSettings.scanRanges.enabled' })}
                          </TableHead>
                          <TableHead className="w-32">
                            {intl.formatMessage({ id: 'deviceSettings.scanRanges.band' })}
                          </TableHead>
                          <TableHead className="w-32">
                            {intl.formatMessage({ id: 'deviceSettings.scanRanges.group' })}
                          </TableHead>
                          <TableHead className="w-28">
                            {intl.formatMessage({ id: 'deviceSettings.scanRanges.channel' })}
                          </TableHead>
                          <TableHead className="w-32">
                            {intl.formatMessage({ id: 'deviceSettings.scanRanges.freqMhz' })}
                          </TableHead>
                          <TableHead className="w-28 text-center">
                            {intl.formatMessage({ id: 'deviceSettings.scanRanges.digital' })}
                          </TableHead>
                          <TableHead className="w-16 text-right"></TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {Object.entries(groupedChannels)
                          .sort(([a], [b]) => a.localeCompare(b))
                          .map(([band, indices]) => (
                            <Fragment key={`band-${band}`}>
                              <TableRow className="bg-background-light">
                                <TableCell
                                  colSpan={7}
                                  className="text-xs font-semibold text-foreground"
                                >
                                  {intl.formatMessage({
                                    id: 'deviceSettings.scanRanges.bandLabel',
                                  })}
                                  : {band}
                                </TableCell>
                              </TableRow>
                              {indices.map((index) => {
                                const field = channelFields[index];
                                const fieldErrors = Array.isArray(errors.channels)
                                  ? errors.channels[index]
                                  : undefined;
                                const bandError = fieldErrors?.band?.message;
                                const groupError = fieldErrors?.group?.message;
                                const channelError = fieldErrors?.channel?.message;
                                const freqError = fieldErrors?.freq?.message;
                                const channelMessages = [
                                  bandError,
                                  groupError,
                                  channelError,
                                  freqError,
                                ].filter(
                                  (message): message is string =>
                                    typeof message === 'string' && message.length > 0,
                                );

                                return (
                                  <Fragment key={field?.id ?? index}>
                                    <TableRow>
                                      <TableCell className="text-center">
                                        <input
                                          id={`enabled-${index}`}
                                          type="checkbox"
                                          aria-label="Enabled"
                                          disabled={disableInputs}
                                          {...register(`channels.${index}.enabled`)}
                                        />
                                      </TableCell>
                                      <TableCell>
                                        <Input
                                          id={`band-${index}`}
                                          disabled={disableInputs}
                                          defaultValue={
                                            typeof field?.band === 'string' ? field.band : ''
                                          }
                                          className={cn(bandError && errorInputClass)}
                                          {...register(`channels.${index}.band`)}
                                        />
                                      </TableCell>
                                      <TableCell>
                                        <Input
                                          id={`group-${index}`}
                                          disabled={disableInputs}
                                          defaultValue={
                                            typeof field?.group === 'string' ? field.group : ''
                                          }
                                          className={cn(groupError && errorInputClass)}
                                          {...register(`channels.${index}.group`)}
                                        />
                                      </TableCell>
                                      <TableCell>
                                        <Input
                                          id={`channel-${index}`}
                                          type="number"
                                          disabled={disableInputs}
                                          defaultValue={
                                            typeof field?.channel === 'number' ? field.channel : 0
                                          }
                                          className={cn(channelError && errorInputClass)}
                                          {...register(`channels.${index}.channel`)}
                                        />
                                      </TableCell>
                                      <TableCell>
                                        <Input
                                          id={`freq-${index}`}
                                          type="number"
                                          step="any"
                                          disabled={disableInputs}
                                          defaultValue={
                                            typeof field?.freq === 'number' ? field.freq : 0
                                          }
                                          className={cn(freqError && errorInputClass)}
                                          {...register(`channels.${index}.freq`)}
                                        />
                                      </TableCell>
                                      <TableCell className="text-center">
                                        <input
                                          id={`digital-${index}`}
                                          type="checkbox"
                                          aria-label="Digital channel"
                                          disabled={disableInputs}
                                          {...register(`channels.${index}.isDigital`)}
                                        />
                                      </TableCell>
                                      <TableCell className="text-right">
                                        <Button
                                          size="icon"
                                          variant="ghost"
                                          type="button"
                                          onClick={() => removeChannel(index)}
                                          aria-label="Remove channel"
                                          title="Remove channel"
                                          disabled={disableInputs}
                                        >
                                          <Trash className="h-4 w-4 text-destructive" />
                                        </Button>
                                      </TableCell>
                                    </TableRow>
                                    {channelMessages.length > 0 ? (
                                      <TableRow>
                                        <TableCell colSpan={7} className="pt-0">
                                          <div className="space-y-1">
                                            {channelMessages.map((message, messageIdx) => (
                                              <p
                                                key={messageIdx}
                                                className="text-sm text-destructive"
                                              >
                                                {message}
                                              </p>
                                            ))}
                                          </div>
                                        </TableCell>
                                      </TableRow>
                                    ) : null}
                                  </Fragment>
                                );
                              })}
                            </Fragment>
                          ))}
                      </TableBody>
                    </Table>
                  )}
                </div>
              </div>

              {/* Ranges Section */}
              <div className="flex-1 flex flex-col min-h-0">
                <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between mb-3">
                  <div className="text-sm font-medium">
                    {intl.formatMessage({ id: 'deviceSettings.scanRanges.ranges' })}
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      type="button"
                      onClick={handleClearRanges}
                      disabled={disableInputs}
                    >
                      {intl.formatMessage({ id: 'deviceSettings.scanRanges.clear' })}
                    </Button>
                    <Button
                      size="sm"
                      variant="secondary"
                      type="button"
                      onClick={() =>
                        appendRange({
                          startFrequency: 0,
                          endFrequency: 0,
                          step: 10,
                          isDigital: false,
                          enabled: true,
                        })
                      }
                      disabled={disableInputs}
                    >
                      {intl.formatMessage({ id: 'deviceSettings.scanRanges.addRange' })}
                    </Button>
                  </div>
                </div>

                <div className="flex-1 overflow-y-auto rounded-md">
                  {watchedRanges.length === 0 ? (
                    <div className="text-sm text-gray-500 p-4">
                      {intl.formatMessage({ id: 'deviceSettings.scanRanges.noRanges' })}
                    </div>
                  ) : (
                    <Table>
                      <TableHeader className="sticky top-0 bg-background-light z-10">
                        <TableRow>
                          <TableHead className="w-24 text-center">
                            {intl.formatMessage({ id: 'deviceSettings.scanRanges.enabled' })}
                          </TableHead>
                          <TableHead className="w-40">
                            {intl.formatMessage({ id: 'deviceSettings.scanRanges.startFreq' })}
                          </TableHead>
                          <TableHead className="w-40">
                            {intl.formatMessage({ id: 'deviceSettings.scanRanges.endFreq' })}
                          </TableHead>
                          <TableHead className="w-32">
                            {intl.formatMessage({ id: 'deviceSettings.scanRanges.step' })}
                          </TableHead>
                          <TableHead className="w-28 text-center">
                            {intl.formatMessage({ id: 'deviceSettings.scanRanges.digital' })}
                          </TableHead>
                          <TableHead className="w-16 text-right"></TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {rangeFields.map((field, index) => {
                          const entryErrors = errors.ranges?.[index];
                          const startHasError = Boolean(entryErrors?.startFrequency);
                          const endHasError = Boolean(entryErrors?.endFrequency);
                          const stepHasError = Boolean(entryErrors?.step);

                          return (
                            <Fragment key={field.id}>
                              <TableRow>
                                <TableCell className="text-center">
                                  <input
                                    id={`range-enabled-${index}`}
                                    type="checkbox"
                                    aria-label="Enabled"
                                    disabled={disableInputs}
                                    {...register(`ranges.${index}.enabled`)}
                                  />
                                </TableCell>
                                <TableCell>
                                  <Input
                                    id={`range-start-${index}`}
                                    type="number"
                                    step="any"
                                    disabled={disableInputs}
                                    defaultValue={
                                      typeof field.startFrequency === 'number'
                                        ? field.startFrequency
                                        : 0
                                    }
                                    className={cn(startHasError && errorInputClass)}
                                    {...register(`ranges.${index}.startFrequency`)}
                                  />
                                </TableCell>
                                <TableCell>
                                  <Input
                                    id={`range-end-${index}`}
                                    type="number"
                                    step="any"
                                    disabled={disableInputs}
                                    defaultValue={
                                      typeof field.endFrequency === 'number'
                                        ? field.endFrequency
                                        : 0
                                    }
                                    className={cn(endHasError && errorInputClass)}
                                    {...register(`ranges.${index}.endFrequency`)}
                                  />
                                </TableCell>
                                <TableCell>
                                  <Input
                                    id={`range-step-${index}`}
                                    type="number"
                                    step="any"
                                    disabled={disableInputs}
                                    defaultValue={typeof field.step === 'number' ? field.step : 10}
                                    className={cn(stepHasError && errorInputClass)}
                                    {...register(`ranges.${index}.step`)}
                                  />
                                </TableCell>
                                <TableCell className="text-center">
                                  <input
                                    id={`range-digital-${index}`}
                                    type="checkbox"
                                    aria-label="Digital range"
                                    disabled={disableInputs}
                                    {...register(`ranges.${index}.isDigital`)}
                                  />
                                </TableCell>
                                <TableCell className="text-right">
                                  <Button
                                    size="icon"
                                    variant="ghost"
                                    type="button"
                                    onClick={() => removeRange(index)}
                                    aria-label="Remove range"
                                    title="Remove range"
                                    disabled={disableInputs}
                                  >
                                    <Trash className="h-4 w-4 text-destructive" />
                                  </Button>
                                </TableCell>
                              </TableRow>
                              {entryErrors ? (
                                <TableRow>
                                  <TableCell colSpan={6} className="pt-0">
                                    <div className="space-y-1">
                                      {[
                                        entryErrors.startFrequency?.message,
                                        entryErrors.endFrequency?.message,
                                        entryErrors.step?.message,
                                      ]
                                        .filter(
                                          (message): message is string =>
                                            typeof message === 'string',
                                        )
                                        .map((message, messageIdx) => (
                                          <p key={messageIdx} className="text-sm text-destructive">
                                            {message}
                                          </p>
                                        ))}
                                    </div>
                                  </TableCell>
                                </TableRow>
                              ) : null}
                            </Fragment>
                          );
                        })}
                      </TableBody>
                    </Table>
                  )}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="sticky bottom-0 bg-background-light border-t border-border p-4 flex justify-end gap-2">
        <Button
          type="submit"
          disabled={!isValid || updateScanRanges.isPending || isFetching}
          size="sm"
        >
          {updateScanRanges.isPending
            ? intl.formatMessage({ id: 'common.saving' })
            : intl.formatMessage({ id: 'common.save' })}
        </Button>
      </div>
    </form>
  );
}
